import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Organization } from '../types/admin';

/** Platform quote defaults — same shape as Organization for card component compatibility. */
export type PlatformQuoteDefaults = Partial<Organization>;

interface PlatformQuoteDefaultsResponse {
  success: boolean;
  data: PlatformQuoteDefaults;
}

export function usePlatformQuoteDefaults() {
  const [defaults, setDefaults] = useState<PlatformQuoteDefaults | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDefaults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<PlatformQuoteDefaultsResponse>('/admin/platform-quote-defaults');
      setDefaults(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load platform quote defaults');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDefaults();
  }, [fetchDefaults]);

  const updateDefaults = useCallback(
    async (data: Record<string, unknown>) => {
      const res = await api.patch<PlatformQuoteDefaultsResponse>('/admin/platform-quote-defaults', data);
      setDefaults(res.data);
      return res.data;
    },
    [],
  );

  return { defaults, loading, error, updateDefaults, refetch: fetchDefaults };
}
